import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle, Clock, Phone, PhoneMissed, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Badge } from '../components/Badge';
import { type Column, DataTable } from '../components/DataTable';
import { StatCard } from '../components/StatCard';
import {
  type AdminVoiceCallRow,
  useAdminVoiceCalls,
  useAdminVoiceCallsStats,
  type VoiceCallStatus,
} from '../hooks/useAdminData';
import { navigate } from '../lib/navigation';
import { VOICE_CALL_STATUS_VARIANT } from '../lib/voiceCallStatus';

const TOOLTIP_STYLE = {
  backgroundColor: 'hsl(225, 20%, 16%)',
  border: '1px solid hsl(225, 15%, 25%)',
  borderRadius: '8px',
  color: 'hsl(0, 0%, 95%)',
};

const GRID_STROKE = 'hsl(225, 15%, 18%)';
const AXIS_STROKE = 'hsl(0, 0%, 45%)';
const PRIMARY_COLOR = 'hsl(184, 92%, 56%)';
const SUCCESS_COLOR = 'hsl(142, 71%, 45%)';
const WARNING_COLOR = 'hsl(38, 92%, 50%)';
const INFO_COLOR = 'hsl(217, 91%, 60%)';

const STATUS_OPTIONS: Array<{ value: '' | VoiceCallStatus; label: string }> = [
  { value: '', label: 'All statuses' },
  ...(Object.keys(VOICE_CALL_STATUS_VARIANT) as VoiceCallStatus[]).map((status) => ({
    value: status,
    label: status,
  })),
];

const OUTCOME_OPTIONS = [
  { value: '', label: 'All outcomes' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'not_qualified', label: 'Not qualified' },
  { value: 'callback_requested', label: 'Callback requested' },
  { value: 'meeting_booked', label: 'Meeting booked' },
];

const PAGE_SIZE = 20;

function formatDuration(seconds: number | null) {
  if (seconds == null) return '-';
  if (seconds < 60) return `${seconds}s`;
  return `${(seconds / 60).toFixed(1)}m`;
}

function ChartSkeleton() {
  return (
    <div className="h-64 flex items-center justify-center">
      <div className="text-text-muted">Loading...</div>
    </div>
  );
}

export function VoiceCalls() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'' | VoiceCallStatus>('');
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [hasErrorFilter, setHasErrorFilter] = useState(false);
  const [organizationId, setOrganizationId] = useState('');
  const [search, setSearch] = useState('');

  const queryParams = {
    page,
    limit: PAGE_SIZE,
    ...(statusFilter && { status: statusFilter }),
    ...(outcomeFilter && { outcome: outcomeFilter }),
    ...(hasErrorFilter && { hasError: true }),
    ...(organizationId && { organizationId }),
    ...(search && { search }),
  };

  const {
    data: calls,
    isLoading: callsLoading,
    isError: callsIsError,
  } = useAdminVoiceCalls(queryParams);
  const {
    data: stats,
    isLoading: statsLoading,
    isError: statsIsError,
  } = useAdminVoiceCallsStats(organizationId || undefined);

  const hasError = callsIsError || statsIsError;

  const statusPieData = stats
    ? [
        { name: 'Completed', value: stats.completed, color: SUCCESS_COLOR },
        { name: 'Scheduled', value: stats.scheduled, color: INFO_COLOR },
        { name: 'In Progress', value: stats.inProgress, color: PRIMARY_COLOR },
        { name: 'No Answer', value: stats.noAnswer, color: WARNING_COLOR },
      ].filter((s) => s.value > 0)
    : [];

  const outcomeBarData = stats
    ? Object.entries(stats.outcomes).map(([outcome, count]) => ({ name: outcome, count }))
    : [];

  const columns: Column<AdminVoiceCallRow>[] = [
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Badge variant={VOICE_CALL_STATUS_VARIANT[row.status]}>{row.status}</Badge>
      ),
    },
    {
      key: 'lead',
      header: 'Lead',
      render: (row) => (
        <span className="text-text font-medium">
          {row.lead?.fullName ?? row.lead?.phone ?? row.leadId}
        </span>
      ),
    },
    {
      key: 'organization',
      header: 'Organization',
      render: (row) => (
        <span className="text-text-secondary">{row.organization?.name ?? row.organizationId}</span>
      ),
    },
    {
      key: 'voiceAgent',
      header: 'Agent',
      render: (row) => <span className="text-text-secondary">{row.voiceAgent?.name ?? '-'}</span>,
    },
    {
      key: 'outcome',
      header: 'Outcome',
      render: (row) => <span className="text-text-secondary">{row.outcome ?? '-'}</span>,
    },
    {
      key: 'durationSeconds',
      header: 'Duration',
      render: (row) => (
        <span className="text-text-secondary">{formatDuration(row.durationSeconds)}</span>
      ),
    },
    {
      key: 'errorMessage',
      header: 'Error',
      render: (row) =>
        row.errorMessage ? (
          <span className="text-danger truncate max-w-[220px] inline-block" title={row.errorMessage}>
            {row.errorCode ? `[${row.errorCode}] ` : ''}
            {row.errorMessage}
          </span>
        ) : (
          '-'
        ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (row) => (
        <span className="text-text-muted text-xs whitespace-nowrap">
          {formatDistanceToNow(new Date(row.createdAt), { addSuffix: true })}
        </span>
      ),
    },
  ];

  if (hasError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Voice Calls</h1>
          <p className="text-sm text-text-muted mt-1">
            Cross-organization voice call monitoring
          </p>
        </div>
        <div className="bg-surface rounded-xl border border-border px-5 py-8 text-center text-sm">
          <p className="text-danger font-medium">Failed to load voice call data</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-3 text-text-secondary underline hover:text-text transition-colors text-xs"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Voice Calls</h1>
        <p className="text-sm text-text-muted mt-1">Cross-organization voice call monitoring</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Total Calls" value={stats?.total ?? 0} icon={Phone} loading={statsLoading} />
        <StatCard
          label="In Progress"
          value={stats?.inProgress ?? 0}
          icon={TrendingUp}
          loading={statsLoading}
        />
        <StatCard
          label="Completed"
          value={stats?.completed ?? 0}
          icon={Phone}
          loading={statsLoading}
        />
        <StatCard
          label="No Answer"
          value={stats?.noAnswer ?? 0}
          icon={PhoneMissed}
          loading={statsLoading}
        />
        <StatCard
          label="Avg Duration"
          value={formatDuration(stats?.avgDurationSeconds ?? null)}
          icon={Clock}
          loading={statsLoading}
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-surface rounded-xl border border-border p-5">
          <h2 className="text-sm font-medium text-text-secondary mb-4">Status Distribution</h2>
          {statsLoading ? (
            <ChartSkeleton />
          ) : statusPieData.length > 0 ? (
            <>
              <ul className="sr-only">
                {statusPieData.map((entry) => (
                  <li key={entry.name}>
                    {entry.name}: {entry.value}
                  </li>
                ))}
              </ul>
              <ResponsiveContainer width="100%" height={260} aria-hidden="true">
              <PieChart>
                <Pie
                  data={statusPieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={90}
                  dataKey="value"
                  nameKey="name"
                  paddingAngle={2}
                  label={({ name, value }) => `${name} (${value})`}
                >
                  {statusPieData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={TOOLTIP_STYLE} />
              </PieChart>
              </ResponsiveContainer>
            </>
          ) : (
            <div className="h-64 flex items-center justify-center text-text-muted">No data</div>
          )}
        </div>

        <div className="bg-surface rounded-xl border border-border p-5">
          <h2 className="text-sm font-medium text-text-secondary mb-4">Outcome Breakdown</h2>
          {statsLoading ? (
            <ChartSkeleton />
          ) : outcomeBarData.length > 0 ? (
            <>
              <ul className="sr-only">
                {outcomeBarData.map((entry) => (
                  <li key={entry.name}>
                    {entry.name}: {entry.count}
                  </li>
                ))}
              </ul>
              <ResponsiveContainer width="100%" height={260} aria-hidden="true">
              <BarChart data={outcomeBarData}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis dataKey="name" stroke={AXIS_STROKE} fontSize={11} />
                <YAxis stroke={AXIS_STROKE} fontSize={12} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="count" name="Calls" radius={[4, 4, 0, 0]} fill={PRIMARY_COLOR} />
              </BarChart>
              </ResponsiveContainer>
            </>
          ) : (
            <div className="h-64 flex items-center justify-center text-text-muted">No data</div>
          )}
        </div>
      </div>

      {/* Filters + table */}
      <div className="bg-surface rounded-xl border border-border">
        <div className="px-5 py-4 border-b border-border flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={search}
            aria-label="Search calls"
            placeholder="Search lead, phone, agent..."
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary min-w-[200px]"
          />
          <input
            type="text"
            value={organizationId}
            aria-label="Filter by organization"
            placeholder="Organization ID"
            onChange={(e) => {
              setOrganizationId(e.target.value);
              setPage(1);
            }}
            className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary min-w-[160px]"
          />
          <select
            value={statusFilter}
            aria-label="Filter by status"
            onChange={(e) => {
              setStatusFilter(e.target.value as '' | VoiceCallStatus);
              setPage(1);
            }}
            className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={outcomeFilter}
            aria-label="Filter by outcome"
            onChange={(e) => {
              setOutcomeFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary"
          >
            {OUTCOME_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-sm text-text-secondary px-1">
            <input
              type="checkbox"
              checked={hasErrorFilter}
              aria-label="Filter errors only"
              onChange={(e) => {
                setHasErrorFilter(e.target.checked);
                setPage(1);
              }}
            />
            <AlertTriangle size={14} className="text-danger" aria-hidden="true" />
            Errors only
          </label>
        </div>

        <DataTable
          columns={columns}
          data={(calls?.data ?? []) as (AdminVoiceCallRow & Record<string, unknown>)[]}
          total={calls?.total ?? 0}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          onRowClick={(row) => navigate(`/voice-calls/${row.id}`)}
          loading={callsLoading}
        />
      </div>
    </div>
  );
}
